import { useCallback, useEffect, useState } from "react";
import { api } from "../api.ts";
import type { SupportTask } from "../types.ts";

function dueLabel(due: string | null | undefined): string {
  if (!due) return "期限なし";
  const d = new Date(due);
  const over = d.getTime() < Date.now();
  return (over ? "⚠ 期限超過 " : "") + d.toLocaleString();
}

export function MyTasksModal({
  onClose,
  onOpenTicket,
  onTaskChanged,
  canWrite,
}: {
  onClose: () => void;
  onOpenTicket: (ticketNumber: string) => void;
  onTaskChanged?: (result: SupportTask) => void;
  canWrite: boolean;
}) {
  const [tasks, setTasks] = useState<SupportTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await api<SupportTask[]>(`/support/tasks?mine=1&status=open&limit=200`));
      setErr("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const complete = async (id: number) => {
    try {
      const updated = await api<SupportTask>(`/support/tasks/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "done" }),
      });
      await load();
      onTaskChanged?.(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="support-modal-backdrop" onClick={onClose}>
      <div className="support-modal support-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal__header">
          <h3>📋 自分の未完了タスク</h3>
          <button type="button" className="support-modal__close" onClick={onClose}>×</button>
        </div>
        <p className="support-modal__hint">
          @ メンションで自動作成されたタスクもここに表示されます。期限超過はリマインド通知の対象です。
        </p>
        {loading ? (
          <div className="support-modal__hint">読み込み中…</div>
        ) : tasks.length === 0 ? (
          <div className="support-modal__hint">未完了タスクはありません</div>
        ) : (
          <ul className="support-my-tasks">
            {tasks.map((t) => (
              <li key={t.id} className="support-my-tasks__item">
                <button
                  type="button"
                  className="support-my-tasks__ticket mono"
                  onClick={() => {
                    if (t.ticket_number) {
                      onOpenTicket(t.ticket_number);
                      onClose();
                    }
                  }}
                >
                  {t.ticket_number || `#${t.ticket_id}`}
                </button>
                <div className="support-my-tasks__body">
                  <div className="support-my-tasks__title">{t.title}</div>
                  <div className="support-my-tasks__meta">{dueLabel(t.due_at)}</div>
                </div>
                {canWrite && (
                  <button type="button" className="support-my-tasks__done" onClick={() => void complete(t.id)}>
                    完了
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {err && <div className="support-tasks-panel__err">{err}</div>}
      </div>
    </div>
  );
}
